import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { dropdownPlacement, type DropdownPlacement } from './child-ticket-dropdown-placement.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 基本設計シートの「項目」「リポジトリ」「担当者」セルの候補リスト。Enter で開いた直後から上下キーで候補を選べて、もう一度 Enter で確定する。幅は最長の候補が折り返さない幅まで伸ばす
 * @graph-connects dropdownPlacement [calls] セル直下への配置
 */
export function ChildTicketSheetDropdown({
  options,
  value,
  ariaLabel,
  anchorRef,
  onCommit,
  onCancel,
}: {
  /** 候補一覧。 */
  options: readonly { value: string; label: string }[];
  /** 選択中の値（未選択は null）。 */
  value: string | null;
  /** 読み上げ用のセル名。 */
  ariaLabel: string;
  /** 候補を出す基準になるセル内の要素。 */
  anchorRef: RefObject<HTMLElement | null>;
  /** 確定ハンドラ。 */
  onCommit: (next: string | null) => void;
  /** 取り消しハンドラ。 */
  onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const listboxId = `child-sheet-lb-${useId()}`;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<DropdownPlacement | null>(null);

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed === '') return [...options];
    return options.filter((option) => option.label.toLowerCase().includes(trimmed));
  }, [options, query]);

  // 開いた時点では選択中の候補に合わせる（今どこに居るかが分かる）。絞り込みを
  // 変えたら、残った先頭へ戻す。
  useEffect(() => {
    const selected = filtered.findIndex((option) => option.value === value);
    setActive(selected >= 0 ? selected : 0);
  }, [filtered, value]);

  // 打ち込んで絞り込めるように、開いた瞬間から検索欄にフォーカスを置く。
  // 上下キーは下のハンドラが先に止めるので、キャレットには吸われない。
  //
  // 置き場所が決まる前は板を `visibility: hidden` で隠しているため、ここで焦ると
  // focus() が無視され、キー入力がセル側に残って上下キーがページのスクロールに
  // 化ける。実寸を測り終えて見えるようになってから 1 度だけ当てる。
  const placed = place !== null;
  useEffect(() => {
    if (!placed) return;
    searchRef.current?.focus();
  }, [placed]);

  // 幅は候補なりに伸ばすので、出てから実寸を測って置き場所を決める。表はスクロール
  // するため scroll / resize でも測り直す。
  useLayoutEffect(() => {
    const update = (): void => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (anchor === null || panel === null) return;
      const rect = anchor.getBoundingClientRect();
      setPlace(
        dropdownPlacement(
          rect,
          { width: panel.offsetWidth, height: panel.offsetHeight },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  // 外側を押したら取り消す（セル選択は表側で保つので、方向キーはそのまま使える）。
  useEffect(() => {
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node | null;
      if (target === null) return;
      if (panelRef.current?.contains(target) === true) return;
      onCancel();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onCancel]);

  const commitActive = useCallback((): void => {
    const option = filtered[active];
    onCommit(option === undefined ? null : option.value);
  }, [filtered, active, onCommit]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    // 候補の中の操作なので、表側の矢印移動へは渡さない
    e.stopPropagation();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (active + step + filtered.length) % filtered.length;
      setActive(next);
      optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      commitActive();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return createPortal(
    <div
      ref={panelRef}
      // 表の罫線や値の上に重なるので、下地はすりガラス。透かしたままだと候補が読めない。
      className="child-ticket-glass child-sheet-dropdown fixed flex flex-col rounded-xl"
      // 表のセル選択を解く判定から外すための目印（候補を押したのにセル選択が
      // 消えると、確定した直後に方向キーが効かなくなる）。
      data-child-ticket-overlay="true"
      role="presentation"
      style={{
        left: place?.left ?? 0,
        top: place?.top ?? 0,
        // 候補の最長文字が折り返さない幅まで伸ばす。画面幅で頭打ちにするだけで、
        // 文字側は折り返さない（折り返すと候補の区別が付かなくなる）。
        width: 'max-content',
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: place?.maxHeight,
        // 実寸を測るまでは見せない（測る前の左上に 1 フレーム出てしまうため）
        visibility: place === null ? 'hidden' : 'visible',
        // Tailwind の任意値だと変数が出力から落ちたとき z-index: auto に化けて
        // サイドバーの下へ潜るので、fallback 付きで直に指定する。
        zIndex: 'var(--z-toast, 300)',
      }}
      onKeyDown={onKeyDown}
    >
      <div className="shrink-0 p-2 pb-0">
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder={t('childTicketPlan.placeholders.search')}
          aria-label={ariaLabel}
          aria-controls={listboxId}
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-border-focus)] focus:outline-none"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="px-3 py-3 text-xs text-[var(--color-text-muted)]">
          {t('childTicketPlan.placeholders.noMatch')}
        </p>
      ) : (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          className="min-h-0 flex-1 overflow-y-auto p-1.5"
        >
          {filtered.map((option, index) => (
            <li
              key={option.value}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              role="option"
              aria-selected={option.value === value}
              data-active={index === active}
              onMouseEnter={() => {
                setActive(index);
              }}
              onClick={() => {
                onCommit(option.value);
              }}
              className="child-sheet-option cursor-pointer rounded-lg px-2.5 py-1.5 text-sm whitespace-nowrap text-[var(--color-text-primary)]"
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  );
}
